import _ from 'lodash';
import Debug from '@/util/debug';
import markdown from '@/components/markdown/Markdown';
import ReachInfo from '@/components/campaign-offer/ReachInfo';
import SvgLegend from '@/components/campaign-offer/SvgLegend';
import { MODE_ONEOFF, MODE_MULTI_MONTH, MODE_SUBSCRIPTION } from '@/services/ModeService';
import { GOAL_REACH_AND_CLICK, GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS, GOAL_REACH_FREQUENCY, GOAL_POST_ENGAGEMENT } from '@/services/GoalTypeService';
import FullInfoBox from '@/components/full-info-box/FullInfoBox.vue';
import SvgReachChart from '@/components/svg-reach-chart/SvgReachChart.vue';
import PackageSliderBox from '@/components/package-slider-box/PackageSliderBox.vue';
import { STEP_TARGET, STEP_OFFER, STEP_FORMAT } from '@/services/StepService';
import Loading from '@/components/loading/Loading.vue';
import { fixPackageFormatFromApiMaf } from '@/services/PackageService';

const debug = Debug('campaign-offer');

export default {
  dependencies: [
    'adApiService',
    'draftService',
    'packageService',
    'modeService',
    'clusterService',
    'goalTypeService',
    'genericConfigService',
  ],
  components: {
    markdown,
    'full-info-box': FullInfoBox,
    'svg-reach-chart': SvgReachChart,
    'package-slider-box': PackageSliderBox,
    'reach-info': ReachInfo, // Separate elements to duplicate in view was created, because on some devices
    'svg-legend': SvgLegend, // column pulling/pushing wasn't working properly...
    loading: Loading,
  },
  props: {
    campaign: Object,
  },
  data() {
    return {
      loading: true,
      error: null,
      businessCategory: null,
      goalTypes: null,
      useSpecialAdCategories: false,
    };
  },
  methods: {
    goBack() {
      this.draftService.updateDraft(this.$route.params.id, this.campaign)
        .then(id => this.$router.push({ name: STEP_TARGET, params: { id } }));
    },
    save() {
      this.campaign.currentStep = STEP_OFFER;
      this.draftService.updateDraft(this.$route.params.id, this.campaign)
        .then(id => this.$router.push({ name: STEP_FORMAT, params: { id } }));
    },
    resetPackage() {
      this.campaign.package = _.get(this.availablePackages, '[0].name', null);
    },
    offerByBudget(goalType) {
      return _.get(this.goalTypes, `${goalType}.offer_by`, 'REACH') === 'BUDGET';
    },
  },
  computed: {
    availableClusters() {
      const order = c => {
        const modeOrder = [MODE_ONEOFF, MODE_MULTI_MONTH, MODE_SUBSCRIPTION];
        return modeOrder.indexOf(c.fullModeName.split(':')[0]);
      };

      const clusters = this.clusterService.availableClusters(this.campaign)
        .map(c => ({
          ...c,
          packages: c.packages
            .filter(p => p.reach.min < this.campaign.potentialReach / c.reachDivider),
          text: this.$t('fn.complex.mode', { mode: c.fullModeName.split(':')[0], duration: c.subscription_duration }),
        }))
        .map(c => ({
          ...c,
          disabled: !c.packages.length,
        }));

      return _.orderBy(clusters, ['subscription_duration'], ['asc'])
        .sort((a, b) => order(a) - order(b));
    },
    selectedCluster() {
      return this.availableClusters.find(c => c.fullModeName === this.internalMode);
    },
    availablePackages: function computedPackages() {
      return this.selectedCluster ? this.selectedCluster.packages : [];
    },
    packagesArr: function computedPackagesArr() {
      return this.availablePackages.map(p =>
        ({ name: p.name, reach: p.reach.min, price: Number(p.budget.budget) }),
      );
    },
    maxReach: function computedMaxReach() {
      return this.availablePackages.length > 0 ? this.availablePackages[0].reach.min : 0;
    },
    selectedPackage: function computeSelectedPackage() {
      const result = this.availablePackages.find(p => p.name === this.campaign.package);
      return result || fixPackageFormatFromApiMaf({});
    },
    extralineBottom() {
      return this.$t(`pos.offer.extraline-bottom.${this.goalType}`) !== `pos.offer.extraline-bottom.${this.goalType}` ? this.$t(`pos.offer.extraline-bottom.${this.goalType}`) : null;
    },
    showSpecialAdCategoryDisclaimer() {
      return this.useSpecialAdCategories;
    },
    goalType() {
      return _.get(this.campaign, 'goal_type');
    },
    isReachCampaign() {
      const goal = _.get(this.campaign, 'goal_type');
      return goal === GOAL_REACH_FREQUENCY || goal === GOAL_POST_ENGAGEMENT;
    },
    isLeadCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_LEAD_GENERATION;
    },
    isOfferCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_LOCAL_OFFERS;
    },
    isClicksCampaign() {
      return _.get(this.campaign, 'goal_type') === GOAL_REACH_AND_CLICK;
    },
    audienceTitle() {
      let key = 'pos.offer.audience-guaranteed';

      if (this.offerByBudget(this.campaign.goal_type)) {
        key = 'pos.offer.campaign-budget';
      }

      return this.$t(key);
    },
    estimatedClicks() {
      const clickRes = {
        min: _.get(this.selectedPackage, 'clicks.min', 0),
        max: _.get(this.selectedPackage, 'clicks.max', 0),
      };
      const multi = parseFloat(_.get(this.businessCategory, 'clicks_multiplier', 0));
      if (!isNaN(multi) && multi > 0) {
        clickRes.min *= multi;
        clickRes.max *= multi;
      }
      return clickRes;
    },
    estimatedLeads() {
      return {
        min: _.get(this.selectedPackage, 'leads.min', 0),
        max: _.get(this.selectedPackage, 'leads.max', 0),
      };
    },
    estimatedOffers() {
      return {
        min: _.get(this.selectedPackage, 'offers.min', 0),
        max: _.get(this.selectedPackage, 'offers.max', 0),
      };
    },
    internalMode: {
      get() {
        if (this.campaign.mode === MODE_SUBSCRIPTION
            && Object.prototype.hasOwnProperty.call(this.campaign.details, 'duration')) {
          const cluster = this.availableClusters
            .find(c => c.subscription_duration === this.campaign.details.duration);
          return cluster ? cluster.fullModeName : MODE_SUBSCRIPTION;
        }
        return this.campaign.mode;
      },
      set(value) {
        const newDetails = {
          ...this.campaign.details,
        };

        if (value.includes(MODE_MULTI_MONTH)) {
          const cluster = this.availableClusters.find(m => m.fullModeName === value);
          newDetails.duration = cluster.subscription_duration;
          this.campaign.mode = MODE_SUBSCRIPTION;
        } else {
          this.campaign.mode = value;
          delete newDetails.duration;
        }
        this.campaign.details = newDetails;

        return value;
      },
    },
  },
  mounted() {
    const fin = () => {
      debug.info('finished loading. error: %o', this.error);
      this.loading = false;
    };
    if (this.campaign.business_category_id) {
      this.adApiService.getBusinessCategory(this.campaign.business_category_id)
        .then(res => {
          this.businessCategory = res;
        });
    }
    Promise.all([
      this.goalTypeService.getOptions()
        .then(res => {
          this.goalTypes = res;
        }),
      this.clusterService.getClusters()
        .then(() => this.modeService.getDefault(this.campaign))
        .then(defaultMode => {
          // Check if mode is valid
          const foundMode = this.availableClusters.find(m =>
            !m.disabled
            && (m.fullModeName === this.campaign.mode || m.fullModeName === this.internalMode),
          );
          if (!foundMode) {
            this.internalMode = defaultMode;
            if (this.campaign.currentStep !== STEP_TARGET) {
              this.$toast.warn({ message: this.$t('pos.offer.campaign-reset-mode') });
            }
          }
          // Check if package is still valid
          const foundPackage = this.availablePackages.find(p => p.name === this.campaign.package);
          if (!foundPackage) {
            this.resetPackage();
            if (this.campaign.currentStep !== STEP_TARGET) {
              this.$toast.warn({ message: this.$t('pos.offer.campaign-reset-package') });
            }
          }
        }),
      this.genericConfigService.getConfig()
        .then(config => this.useSpecialAdCategories = config.use_special_ad_categories),
    ]).then(fin)
      .catch(err => {
        this.error = err;
        fin();
      });
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-offer/CampaignOffer.js